/**
 * 图片画廊组件
 * 支持搜索缩略图和保存的高清图片
 * 
 * 优化后的版本，拆分为更小的子组件：
 * - ImageGalleryTabs: Tab 切换
 * - ImageGrid: 图片网格
 * - Lightbox: 灯箱查看
 */

import React, { useState, useMemo, useEffect, useRef } from 'react';
import type { SearchResult, SavedSearchImage } from '@/types/agent';
import { ImageGalleryTabs } from './ImageGalleryTabs';
import { ImageGrid, type DisplayImage } from './ImageGrid';
import { Lightbox } from './Lightbox';
import { useImageError } from '../hooks/useImageError';
import { SEARCH_RESULT_CONFIG } from '../config/searchResultConfig';

interface ImageGalleryProps {
  /** 搜索结果列表 */
  results: SearchResult[];
  /** 保存的高清图片列表 */
  savedImages?: SavedSearchImage[];
  /** 搜索类型（图片搜索时允许外部 URL） */
  searchType?: string;
}

// 显示图片数据接口
interface ProcessedImage {
  src: string;
  title: string;
  isSaved: boolean;
  originalIndex: number;
  /** I7+8: 图片相关性评分，由视觉模型打分 */
  score?: number;
  /** I8: 是否为低相关性图片（评分 1-3 分） */
  lowRelevance?: boolean;
}

/**
 * 图片画廊组件
 * 支持 Tab 切换显示搜索缩略图和保存的高清图片
 */
export const ImageGallery: React.FC<ImageGalleryProps> = ({ results, savedImages, searchType }) => {
  // State
  const [activeTab, setActiveTab] = useState<'search' | 'saved'>('search');
  const [lightboxSrc, setLightboxSrc] = useState<string | null>(null);
  const [lightboxTitle, setLightboxTitle] = useState('');

  // P3: savedImages 从0变为非0时，自动切换到saved Tab
  const prevSavedCountRef = useRef(0);
  const currentSavedCount = savedImages?.length || 0;

  useEffect(() => {
    if (prevSavedCountRef.current === 0 && currentSavedCount > 0) {
      setActiveTab('saved');
    }
    prevSavedCountRef.current = currentSavedCount;
  }, [currentSavedCount]);

  // Hooks
  const { handleImageError, isIndexFailed } = useImageError();

  // 计算属性
  const hasSavedImages = savedImages && savedImages.length > 0;

  // 处理搜索结果图片
  const processedSearchImages = useMemo<ProcessedImage[]>(() => {
    const { maxDisplayImages } = SEARCH_RESULT_CONFIG.imageGallery;
    const isImageSearch = searchType === 'image';
    
    return results
      .map((r, i) => ({ result: r, originalIndex: i }))
      .filter(({ result: r, originalIndex: i }) => {
        const imgSrc = r.full_image || r.thumbnail || '';
        if (!imgSrc) return false;
        // 图片搜索：允许所有 URL（通过 referrerpolicy 绕过防盗链）
        // 通用搜索：只允许本地路径（外部 URL 受防盗链保护）
        const isLoadable = isImageSearch || imgSrc.startsWith('/assets/') || imgSrc.startsWith('data:');
        const notFailed = !isIndexFailed(i);
        return isLoadable && notFailed;
      })
      .slice(0, maxDisplayImages)
      .map(({ result: r, originalIndex }) => {
        let src = r.full_image || r.thumbnail || '';

        return {
          src,
          title: r.title,
          isSaved: false,
          originalIndex,
          // I7+8: 从搜索结果透传评分和低相关性标记
          // Fix 8: 优先读 _vision_score（后端 VLM/文本元信息评分），无则读 score（引擎原始排序）
          score: r._vision_score ?? r.score,
          lowRelevance: r._low_relevance,
        };
      });
  }, [results, isIndexFailed]);

  // 处理保存的高清图片
  const processedSavedImages = useMemo<ProcessedImage[]>(() => {
    if (!savedImages) return [];

    return savedImages.map((img) => ({
      src: img.url,
      title: img.source_platform ? `${img.source_platform} - ${img.query}` : img.query,
      isSaved: true,
      originalIndex: -1,
      // I7+8: SavedSearchImage 也可能有 score（如果后端保存时带上了评分）
      score: img.score,
      lowRelevance: undefined,
    }));
  }, [savedImages]);

  // 根据当前 Tab 选择显示的图片
  const displayImages: DisplayImage[] = activeTab === 'saved' && hasSavedImages
    ? processedSavedImages
    : processedSearchImages;

  // 过滤有效图片
  const validImages = displayImages.filter((img) => img.src);

  // 如果没有图片且没有保存图片，返回 null
  if (validImages.length === 0 && !hasSavedImages) {
    return null;
  }

  // 处理图片错误
  const handleError = (index: number, originalIndex: number) => {
    const img = displayImages[index];
    handleImageError(index, img?.src);
  };

  // 打开灯箱
  const openLightbox = (src: string, title: string) => {
    setLightboxSrc(src);
    setLightboxTitle(title);
  };

  // 关闭灯箱
  const closeLightbox = () => {
    setLightboxSrc(null);
    setLightboxTitle('');
  };

  return (
    <>
      {/* Tab 切换 - 仅在有保存图片时显示 */}
      {hasSavedImages && (
        <ImageGalleryTabs
          activeTab={activeTab}
          onTabChange={setActiveTab}
          searchCount={processedSearchImages.length}
          savedCount={processedSavedImages.length}
        />
      )}

      {/* 图片网格 */}
      <ImageGrid
        images={validImages}
        onImageError={handleError}
        onImageClick={openLightbox}
      />

      {/* 灯箱 */}
      <Lightbox
        src={lightboxSrc}
        title={lightboxTitle}
        onClose={closeLightbox}
      />
    </>
  );
};
